"use client"

import React, { useState, useEffect } from 'react';
import Loading from "../../../../components/Loading/loading";
import { CloseOutlined, SearchIcon, EditIconOutlined, TrashIcon } from "../../utils/icons";
import videoGroupService from "../../utils/services/videoGroup";
import groupSceneServices from "../../utils/services/groupScene";

interface VideoGroup {
    id: number;
    group_id: number;
    link_video: string;
    link_video_webm: string;
    thumbnail: string;
    volume: number;
    type: string;
    is_show: number;
    created_by: number;
    created_at: string;
    updated_by: number;
    updated_at: string;
    group_title?: string;
    group_thumbnail?: string;
}

interface Group {
    id: number;
    thumbnail: string;
    title?: string;
    is_show: number;
    parent: string;
    index: number;
}

interface PopupListSceneGroupVideoProps {
    onClose: () => void;
    onSelect?: (selectedVideoGroup: VideoGroup) => void;
    onUpdate?: (videoGroup: VideoGroup) => void;
    onDelete?: (videoGroup: VideoGroup) => void;
    language_id?: string;
    idPath: string;
}

const PopupListSceneGroupVideo: React.FC<PopupListSceneGroupVideoProps> = ({
    onClose,
    onSelect,
    onUpdate,
    onDelete,
    language_id = "en",
    idPath
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVideoGroup, setSelectedVideoGroup] = useState<VideoGroup | null>(null);
    const [videoGroups, setVideoGroups] = useState<VideoGroup[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Fetch video groups and groups data on component mount or when idPath changes
    useEffect(() => {
        fetchData();
    }, [idPath, language_id]);

    const fetchData = async () => {
        setIsLoadingData(true);
        try {
            // Robust data extraction helper
            const extractData = (res: any) => {
                if (!res) return [];
                const body = res.data ? res.data : res;
                if (!body) return [];
                if (Array.isArray(body)) return body;
                if (body.success && Array.isArray(body.data)) return body.data;
                if (body.success && body.data && Array.isArray(body.data.data)) return body.data.data;
                if (Array.isArray(body.data)) return body.data;
                return [];
            };

            // Fetch video groups first
            try {
                const videoGroupsResponse = await videoGroupService.getAllVideoGroups(idPath) as any;
                // console.log('Video Groups Raw Response:', videoGroupsResponse);
                const videoData = extractData(videoGroupsResponse);
                setVideoGroups(videoData);
            } catch (err) {
                console.error('Error fetching video groups:', err);
                setVideoGroups([]);
            }

            // Fetch groups titles second (don't block the UI if this fails)
            try {
                const groupsResponse = await groupSceneServices.getAllGroup({ id_path: idPath, language_id }) as any;
                const groupData = extractData(groupsResponse);
                setGroups(groupData);
            } catch (err) {
                console.error('Error fetching groups titles:', err);
                setGroups([]);
            }

        } catch (error) {
            console.error('General error in fetchData:', error);
        } finally {
            setIsLoadingData(false);
        }
    };

    // Helper function to get group title by group_id
    const getGroupTitle = (videoGroup: VideoGroup): string => {
        // Priority 1: Use title returned directly from API (most efficient)
        if (videoGroup.group_title) return videoGroup.group_title;

        // Priority 2: Fallback to manual mapping from groups list
        const group = groups.find(g => g.id === videoGroup.group_id);
        return group?.title || `Group_${videoGroup.group_id}`;
    };

    // Filter video groups based on search term
    const filteredVideoGroups = videoGroups.filter(videoGroup => {
        const term = searchTerm.toLowerCase();
        const groupTitle = getGroupTitle(videoGroup).toLowerCase();
        const groupId = videoGroup.group_id.toString();
        const videoType = videoGroup.type.toLowerCase();
        
        return groupTitle.includes(term) || 
               groupId.includes(term) || 
               videoType.includes(term);
    });

    // Handle video group selection
    const handleVideoGroupSelection = (videoGroup: VideoGroup) => {
        setSelectedVideoGroup(videoGroup);
    };

    // Handle the search input change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // Handle select button click
    const handleSelectVideoGroup = async () => {
        if (!selectedVideoGroup) {
            alert('Vui lòng chọn một video nhóm cảnh');
            return;
        }

        setIsLoading(true);

        try {
            if (onSelect) {
                onSelect(selectedVideoGroup);
            }
            // The parent component handles closing the popup
        } catch (error) {
            console.error('Error selecting video group:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle update button click
    const handleUpdateVideoGroup = (videoGroup: VideoGroup, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent selecting the item when clicking update
        if (onUpdate) {
            onUpdate(videoGroup);
        } else {
            // Default behavior - could show an edit form or navigate to edit page
        }
    };

    // Handle delete button click
    const handleDeleteVideoGroup = async (videoGroup: VideoGroup, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent selecting the item when clicking delete

        if (window.confirm(`Bạn có chắc chắn muốn xóa video nhóm cảnh này?\nNhóm cảnh: ${getGroupTitle(videoGroup)}\nID: ${videoGroup.id}`)) {
            try {
                setIsLoading(true);

                // Call delete API
                const deleteResponse = await videoGroupService.deleteVideoGroup({ group_id: videoGroup.id, idPath }) as any;

                if (deleteResponse?.data?.success) {
                    alert('Video nhóm cảnh đã được xóa thành công!');

                    // Call parent callback if provided
                    if (onDelete) {
                        onDelete(videoGroup);
                    }

                    // Refresh the list
                    await fetchData();
                } else {
                    throw new Error('Failed to delete video group');
                }
            } catch (error) {
                console.error('Error deleting video group:', error);
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                alert(`Lỗi khi xóa video nhóm cảnh: ${errorMessage}`);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm">
            <div className="w-[900px] max-w-[90vw] glass_background rounded-[16px] backdrop-blur-[80px] rounded-[20px] shadow-[0px_4px_48px_0px_rgba(0,0,0,0.2),inset_-1px_1px_3px_0px_rgba(255,255,255,0.8),inset_0px_4px_10px_3px_rgba(255,255,255,0.2)] p-6 flex flex-col gap-6 relative overflow-hidden"
                style={{
                    height: `calc(100dvh - 150px)`,
                    maxHeight: '80vh',
                }}>
                {/* Close button */}
                <button
                    className="absolute top-2 right-2 w-[64px] h-[64px] flex items-center justify-center"
                    onClick={onClose}
                >
                    <div className="w-[48px] h-[48px] rounded-[10px] flex items-center justify-center">
                        <CloseOutlined width="18" height="18" stroke="#464646" strokeWidth="2" />
                    </div>
                </button>

                {/* Title */}
                <h2 className="text-2xl font-bold font-['Roboto_Flex']">
                    Danh sách video nhóm cảnh
                </h2>

                {/* Search box */}
                <div className="flex items-center gap-4 bg-white rounded-[10px] border border-[#ECECEC] p-5 w-full">
                    <SearchIcon width="40" height="40" stroke="black" strokeWidth="2" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Tìm kiếm video nhóm cảnh..."
                        className="bg-transparent text-xl flex-1 outline-none text-[#B0B0B0]"
                    />
                </div>

                {/* List of video groups */}
                <div className="bg-white rounded-2xl border border-[#ECECEC] p-3 flex-1 overflow-y-auto scrollbar-hide max-h-[500px]">
                    {isLoadingData ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 min-h-[200px]">
                            <Loading type="spinner" size="lg" color="#000000" />
                            <p className="text-lg text-gray-600 text-center">
                                Đang tải danh sách video nhóm cảnh...
                            </p>
                        </div>
                    ) : videoGroups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 min-h-[200px]">
                            <div className="text-gray-400 text-lg">📭</div>
                            <p className="text-lg text-gray-600 text-center">
                                Không có video nhóm cảnh nào
                            </p>
                        </div>
                    ) : filteredVideoGroups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 min-h-[200px]">
                            <div className="text-gray-400 text-lg">🔍</div>
                            <p className="text-lg text-gray-600 text-center">
                                Không tìm thấy video nhóm cảnh nào với từ khóa "{searchTerm}"
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2.5">
                            {filteredVideoGroups.map((videoGroup) => {
                                const groupTitle = getGroupTitle(videoGroup);
                                const displayThumbnail = videoGroup.thumbnail || videoGroup.group_thumbnail;
                                return (
                                    <div
                                        key={videoGroup.id}
                                        className={`flex flex-row justify-between items-center p-2.5 rounded-[10px] cursor-pointer hover:bg-gray-50 ${selectedVideoGroup?.id === videoGroup.id ? "bg-blue-100 border-2 border-blue-300" : "border-2 border-transparent"
                                            }`}
                                        onClick={() => handleVideoGroupSelection(videoGroup)}
                                    >
                                        <div className="flex flex-row items-center gap-6 flex-1">
                                            <div className="w-16 h-16 bg-[#D9D9D9] border border-[#ECECEC] rounded-[10px] overflow-hidden relative">
                                                {displayThumbnail ? (
                                                    <>
                                                        <img
                                                            src={displayThumbnail}
                                                            alt={groupTitle}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).style.display = 'none';
                                                                const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                        />

                                                        <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-[#464646] via-[rgba(101,101,101,0.5)] to-transparent flex items-center justify-center" style={{ display: 'none' }}>
                                                            <span className="text-white text-xs">Không có ảnh</span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-b from-[#464646] via-[rgba(101,101,101,0.5)] to-transparent flex items-center justify-center">
                                                        <span className="text-white text-xs">Không có ảnh</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-1 flex-1 min-w-0">
                                                <span
                                                    className="text-lg font-medium truncate cursor-pointer leading-tight"
                                                    title={`Video Group ID: ${videoGroup.id}\nGroup: ${groupTitle}\nVolume: ${videoGroup.volume}\nType: ${videoGroup.type}`}
                                                >
                                                    {groupTitle}
                                                </span>
                                                <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                                                    {/* Update button */}
                                                    <button
                                                        onClick={(e) => handleUpdateVideoGroup(videoGroup, e)}
                                                        className="flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded text-xs transition-colors duration-200"
                                                        title="Chỉnh sửa video nhóm cảnh"
                                                    >
                                                        <EditIconOutlined width="12" height="12" className="text-blue-600" />
                                                        <span className="text-blue-600">Chỉnh sửa</span>
                                                    </button>

                                                    {/* Delete button */}
                                                    <button
                                                        onClick={(e) => handleDeleteVideoGroup(videoGroup, e)}
                                                        className="flex items-center gap-1 px-2 py-1 bg-red-100 hover:bg-red-200 rounded text-xs transition-colors duration-200"
                                                        title="Xóa video nhóm cảnh"
                                                    >
                                                        <TrashIcon width="12" height="12" className="text-red-600" />
                                                        <span className="text-red-600">Xóa</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Video preview indicator */}
                                        <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polygon points="5,3 19,12 5,21"></polygon>
                                            </svg>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className="flex justify-between w-full gap-3">
                    {onSelect && (
                        <button
                            type="button"
                            onClick={handleSelectVideoGroup}
                            disabled={isLoading || !selectedVideoGroup}
                            className={`flex-1 bg-green-500 text-white rounded-[10px] p-2.5 flex items-center justify-center gap-2.5 ${(isLoading || !selectedVideoGroup) ? "opacity-50 cursor-not-allowed" : "hover:bg-green-600"
                                }`}
                        >
                            <span className="text-xl">
                                {isLoading ? (
                                    <>
                                        <Loading type="spinner" size="sm" color="#FFFFFF" />
                                        <span className="ml-2">Đang chọn...</span>
                                    </>
                                ) : (
                                    "Chọn"
                                )}
                            </span>
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className={`${onSelect ? 'flex-1' : 'w-full'} bg-white border border-[#ECECEC] rounded-[10px] p-2.5 flex items-center justify-center ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                            }`}
                    >
                        <span className="text-xl">{onSelect ? 'Hủy bỏ' : 'Đóng'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PopupListSceneGroupVideo;
